import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  Brain, AlertTriangle, CheckCircle, TrendingUp, Filter,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const EvaluationsDashboard = () => {
  const { user } = useAuth();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');
  const [filterReview, setFilterReview] = useState('flagged');
  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0,
    totalCount: 0,
    flaggedCount: 0
  });
  const [selectedEval, setSelectedEval] = useState(null);
  const [evalSettings, setEvalSettings] = useState({
    llm_evaluation_enabled: true,
    llm_evaluator_model: 'x-ai/grok-code-fast-1',
    llm_evaluation_threshold_safety: 0.7,
    llm_evaluation_threshold_general: 0.5
  });

  useEffect(() => {
    fetchEvaluations();
    fetchEvaluationSettings();
  }, [period, filterReview, pagination.offset]);

  const fetchEvaluations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        period,
        limit: pagination.limit,
        offset: pagination.offset
      });

      if (filterReview !== 'all') {
        params.append('requires_review', filterReview === 'flagged');
      }

      const response = await axios.get(
        `${API_BASE}/admin/evaluations?${params.toString()}`
      );

      setEvaluations(response.data.evaluations);
      setPagination(prev => ({
        ...prev,
        totalCount: response.data.total_count,
        flaggedCount: response.data.flagged_count
      }));
    } catch (error) {
      console.error('Failed to fetch evaluations:', error);
      toast.error('Failed to load evaluations');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvaluationSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/settings/evaluation`);
      setEvalSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch evaluation settings:', error);
    }
  };

  const getScoreColor = (score, isSafety = false) => {
    const threshold = isSafety ? evalSettings.llm_evaluation_threshold_safety : evalSettings.llm_evaluation_threshold_general;
    if (score >= threshold) return 'text-green-600';
    if (score >= threshold - 0.2) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadge = (score, isSafety = false) => {
    const threshold = isSafety ? evalSettings.llm_evaluation_threshold_safety : evalSettings.llm_evaluation_threshold_general;
    if (score >= threshold) return 'bg-green-100 text-green-800';
    if (score >= threshold - 0.2) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const nextPage = () => {
    if (pagination.offset + pagination.limit < pagination.totalCount) {
      setPagination(prev => ({
        ...prev,
        offset: prev.offset + prev.limit
      }));
    }
  };

  const prevPage = () => {
    if (pagination.offset > 0) {
      setPagination(prev => ({
        ...prev,
        offset: Math.max(0, prev.offset - prev.limit)
      }));
    }
  };

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.totalCount / pagination.limit);

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Brain className="w-8 h-8 text-emerald-600" />
              LLM-as-Judge Evaluations
            </h1>
            <p className="text-slate-600 mt-2">
              Automated quality assessment of AI responses
            </p>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">
              Total Evaluations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{pagination.totalCount}</div>
            <p className="text-sm text-gray-500 mt-1">
              {period === '7d' ? 'Last 7 days' : period === '30d' ? 'Last 30 days' : period === '90d' ? 'Last 90 days' : 'All time'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">
              Flagged for Review
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-amber-500" />
              <div className="text-3xl font-bold">{pagination.flaggedCount}</div>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {pagination.totalCount > 0
                ? `${((pagination.flaggedCount / pagination.totalCount) * 100).toFixed(1)}% of total`
                : '0% of total'
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">
              System Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {evalSettings.llm_evaluation_enabled ? (
                <>
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  <span className="text-lg font-semibold text-green-700">Active</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-6 w-6 text-gray-400" />
                  <span className="text-lg font-semibold text-gray-500">Disabled</span>
                </>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Judge: {evalSettings.llm_evaluator_model.split('/').pop()}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-8">
        <CardContent className="pt-6">
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <Label>Period:</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">7 days</SelectItem>
                  <SelectItem value="30d">30 days</SelectItem>
                  <SelectItem value="90d">90 days</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label>Status:</Label>
              <Select value={filterReview} onValueChange={setFilterReview}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flagged">Flagged only</SelectItem>
                  <SelectItem value="passed">Passed only</SelectItem>
                  <SelectItem value="all">All evaluations</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={fetchEvaluations}
              className="ml-auto"
            >
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Evaluations List */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <p className="text-gray-500">Loading evaluations...</p>
            </div>
          </CardContent>
        </Card>
      ) : evaluations.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Brain className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No evaluations found</p>
              <p className="text-sm text-gray-400 mt-1">
                Evaluations will appear here after chat interactions
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {evaluations.map((evaluation, index) => (
            <Card
              key={index}
              className={`cursor-pointer transition-all ${
                selectedEval === evaluation ? 'ring-2 ring-blue-500' : ''
              } ${evaluation.requires_review ? 'border-l-4 border-l-amber-500' : ''}`}
              onClick={() => setSelectedEval(selectedEval === evaluation ? null : evaluation)}
            >
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {evaluation.requires_review && (
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                      )}
                      {evaluation.user_message.substring(0, 100)}
                      {evaluation.user_message.length > 100 && '...'}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {new Date(evaluation.evaluated_at).toLocaleString()} •
                      Avg Score: {evaluation.average_score.toFixed(2)}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      evaluation.requires_review
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {evaluation.requires_review ? 'Needs Review' : 'Passed'}
                    </span>
                  </div>
                </div>
              </CardHeader>

              {selectedEval === evaluation && (
                <CardContent className="space-y-6">
                  {/* Scores Grid */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-1">Safety</div>
                      <div className={`text-2xl font-bold ${getScoreColor(evaluation.evaluations.safety.score, true)}`}>
                        {evaluation.evaluations.safety.score.toFixed(2)}
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${getScoreBadge(evaluation.evaluations.safety.score, true)}`}>
                        {evaluation.evaluations.safety.score >= evalSettings.llm_evaluation_threshold_safety ? 'Pass' : 'Fail'}
                      </span>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-1">Helpfulness</div>
                      <div className={`text-2xl font-bold ${getScoreColor(evaluation.evaluations.helpfulness.score)}`}>
                        {evaluation.evaluations.helpfulness.score.toFixed(2)}
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${getScoreBadge(evaluation.evaluations.helpfulness.score)}`}>
                        {evaluation.evaluations.helpfulness.score >= evalSettings.llm_evaluation_threshold_general ? 'Pass' : 'Fail'}
                      </span>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-1">Tone</div>
                      <div className={`text-2xl font-bold ${getScoreColor(evaluation.evaluations.tone.score)}`}>
                        {evaluation.evaluations.tone.score.toFixed(2)}
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${getScoreBadge(evaluation.evaluations.tone.score)}`}>
                        {evaluation.evaluations.tone.score >= evalSettings.llm_evaluation_threshold_general ? 'Pass' : 'Fail'}
                      </span>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-1">Accuracy</div>
                      <div className={`text-2xl font-bold ${getScoreColor(evaluation.evaluations.accuracy.score)}`}>
                        {evaluation.evaluations.accuracy.score.toFixed(2)}
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${getScoreBadge(evaluation.evaluations.accuracy.score)}`}>
                        {evaluation.evaluations.accuracy.score >= evalSettings.llm_evaluation_threshold_general ? 'Pass' : 'Fail'}
                      </span>
                    </div>
                  </div>

                  {/* Reasoning */}
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Safety Assessment:</h4>
                      <p className="text-sm text-gray-700">{evaluation.evaluations.safety.reasoning}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Helpfulness Assessment:</h4>
                      <p className="text-sm text-gray-700">{evaluation.evaluations.helpfulness.reasoning}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Tone Assessment:</h4>
                      <p className="text-sm text-gray-700">{evaluation.evaluations.tone.reasoning}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Accuracy Assessment:</h4>
                      <p className="text-sm text-gray-700">{evaluation.evaluations.accuracy.reasoning}</p>
                    </div>
                  </div>

                  {/* AI Response */}
                  <div>
                    <h4 className="font-semibold text-sm mb-2">AI Response:</h4>
                    <div className="bg-gray-50 p-3 rounded text-sm">
                      {evaluation.ai_response}
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="text-xs text-gray-500 space-y-1 border-t pt-3">
                    <div>User ID: {evaluation.user_id}</div>
                    <div>Session ID: {evaluation.session_id}</div>
                    {evaluation.langfuse_trace_id && (
                      <div>Langfuse Trace: {evaluation.langfuse_trace_id}</div>
                    )}
                    {evaluation.metadata && (
                      <div>Model: {evaluation.metadata.model}</div>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalCount > pagination.limit && (
        <Card className="mt-8">
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.totalCount)} of {pagination.totalCount}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={prevPage}
                  disabled={pagination.offset === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="px-3 py-2 text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={nextPage}
                  disabled={pagination.offset + pagination.limit >= pagination.totalCount}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EvaluationsDashboard;
